import React, { useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { KeyRound, ShieldCheck, ShieldOff } from "lucide-react";

/** MFA (TOTP) setup card. Presents QR-derived otpauth URI + a fallback secret. */
export default function MFASetup() {
  const { user, refresh } = useAuth();
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);

  const start = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/mfa/setup");
      setSetup(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Setup failed"); }
    setBusy(false);
  };

  const verify = async () => {
    setBusy(true);
    try {
      await api.post("/auth/mfa/verify", { code });
      toast.success("MFA enabled");
      setSetup(null); setCode("");
      await refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Invalid code"); }
    setBusy(false);
  };

  const disable = async () => {
    setBusy(true);
    try {
      await api.post("/auth/mfa/disable", { password: pw });
      toast.success("MFA disabled");
      setPw("");
      await refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    setBusy(false);
  };

  if (user?.mfa_enabled) {
    return (
      <div className="space-y-3" data-testid="mfa-enabled">
        <div className="flex items-center gap-2 text-emerald-brand">
          <ShieldCheck className="h-4 w-4" />
          <span className="text-sm font-semibold">Two-factor authentication is on</span>
        </div>
        <p className="text-xs text-muted-foreground">Confirm your password to disable MFA.</p>
        <div className="flex items-center gap-2">
          <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Current password" className="rounded-xl h-9" data-testid="mfa-disable-password" />
          <Button variant="outline" onClick={disable} disabled={busy || !pw} className="rounded-lg" data-testid="mfa-disable-button">
            <ShieldOff className="h-4 w-4 mr-1.5" /> Disable
          </Button>
        </div>
      </div>
    );
  }

  if (!setup) {
    return (
      <div className="space-y-3" data-testid="mfa-off">
        <p className="text-xs text-muted-foreground">
          Add an extra sign-in step with a TOTP app (Google Authenticator, 1Password, Authy).
        </p>
        <Button onClick={start} disabled={busy} className="rounded-lg" data-testid="mfa-setup-button">
          <KeyRound className="h-4 w-4 mr-1.5" /> Enable 2FA
        </Button>
      </div>
    );
  }

  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(setup.otpauth_uri)}`;
  return (
    <div className="space-y-3" data-testid="mfa-setup-panel">
      <p className="text-xs text-muted-foreground">Scan with your authenticator app, then enter the 6-digit code.</p>
      <div className="flex items-start gap-4">
        <img src={qrSrc} alt="TOTP QR" className="rounded-xl border border-border bg-white" width={180} height={180} />
        <div className="flex-1 space-y-2">
          <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Secret (manual entry)</Label>
          <code className="block p-2 rounded-lg bg-muted text-xs font-mono break-all">{setup.secret}</code>
          <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground pt-2 block">Verification code</Label>
          <Input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="000000" className="rounded-xl h-10 font-mono tracking-widest text-center" data-testid="mfa-verify-code" />
          <Button onClick={verify} disabled={busy || code.length !== 6} className="rounded-lg w-full" data-testid="mfa-verify-button">Verify & activate</Button>
        </div>
      </div>
    </div>
  );
}
